import React, { useState, FocusEvent } from "react"
import styled, { css } from "styled-components"
import { AppFunctionComponent } from "../../../types"
import { AnimatedInputLabel } from "../../../components/common/general-styles.component"
import InputProps from "./input.interface"
import { noop, uniqueId } from "lodash"

const InputWrapper = styled.div`
  position: relative;
`

export const inputStyles = css`
  width: 100%;
  padding: 20px 20px 8px 20px;
  box-sizing: border-box;

  border-radius: ${({ theme }) => theme.size.borderRadiusSmall};
  border: 1px solid ${({ theme }) => theme.color.commerceBorder};

  font-size: 1rem;
  font-weight: 300;
  line-height: 2em;
  font-family: ${({ theme }) => theme.font.basic};

  &:focus {
    border-color: ${({ theme }) => theme.color.buttonActive};
    outline: none;
  }
  &:disabled {
    background-color: ${({ theme }) => theme.color.inputDisabledBackground};
    color: ${({ theme }) => theme.color.textDisabled};
  }
`

const InputElement = styled.input<{ error?: boolean }>`
  ${inputStyles};

  border: 1px solid
    ${({ theme, error }) =>
      error ? theme.color.error : theme.color.commerceBorder};

  &::placeholder {
    color: transparent;
  }

  @media (max-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
    padding-left: 15px;
  }
`

const Input: AppFunctionComponent<InputProps> = ({
  className,
  type = "text",
  placeholder,
  defaultValue,
  required,
  error,
  onBlur = noop,
  onFocus = noop,
  inputRef,
  name,
  value,
  ...props
}) => {
  const [focused, setFocus] = useState(Boolean(defaultValue || value))

  const focus = (event: FocusEvent<HTMLInputElement>) => {
    setFocus(true)
    onFocus(event)
  }

  const blur = (event: FocusEvent<HTMLInputElement>) => {
    setFocus(Boolean(value))
    onBlur(event)
  }

  const id = `id-${uniqueId()}`

  return (
    <InputWrapper className={className}>
      <AnimatedInputLabel
        focused={focused}
        empty={!defaultValue && !value}
        htmlFor={id}
      >
        {placeholder} {!required && "(Optional)"}
      </AnimatedInputLabel>
      <InputElement
        role="textbox"
        type={type}
        name={name}
        defaultValue={defaultValue}
        placeholder={placeholder}
        error={Boolean(error)}
        onFocus={focus}
        onBlur={blur}
        id={id}
        value={value}
        {...props}
        ref={inputRef}
      />
    </InputWrapper>
  )
}

export default Input
